import { z } from 'zod';
import { createDefaultInsuranceClaimsClaimCoachSteps, insuranceClaimsClaimCoachStepKeySchema, } from './insurance-claims-claim-coach.contract.js';
import { insuranceClaimsClaimSchema, insuranceClaimsIdSchema, insuranceClaimsPageSchema, } from './insurance-claims-claim.contract.js';
import { insuranceClaimsSupplementSummarySchema, } from './insurance-claims-supplement.contract.js';
export const insuranceClaimsClaimDeskCapability = 'insurance-claims.claim-desk.v1';
export const insuranceClaimsClaimDeskLaneSchema = z.enum([
    'blocked',
    'action_due',
    'intake',
    'estimate_review',
    'supplement',
    'money',
    'production',
    'closeout',
]);
export const insuranceClaimsClaimDeskToneSchema = z.enum([
    'risk', 'attention', 'info', 'good',
]);
export const insuranceClaimsClaimDeskAttentionReasonSchema = z.enum([
    'claim-coach-blocked',
    'claim-coach-overdue',
    'supplement-response-critical',
    'supplement-response-high',
    'supplement-response-medium',
    'supplement-revision-needed',
    'approved-supplement-unpaid',
]);
export const insuranceClaimsClaimDeskCoachSummarySchema = z.strictObject({
    stored: z.boolean(),
    totalSteps: z.literal(10),
    completedSteps: z.number().int().min(0).max(10),
    skippedSteps: z.number().int().min(0).max(10),
    blockedSteps: z.number().int().min(0).max(10),
    overdueSteps: z.number().int().min(0).max(10),
    dueSoonSteps: z.number().int().min(0).max(10),
    completionPercent: z.number().int().min(0).max(100),
    activeStepKey: insuranceClaimsClaimCoachStepKeySchema.nullable(),
    activeStepTitle: z.string().trim().min(1).max(180).nullable(),
    nextAction: z.string().trim().min(1).max(1_000).nullable(),
    nextDueAt: z.string().datetime({ offset: true }).nullable(),
});
export const insuranceClaimsClaimDeskRowSchema = z.strictObject({
    claimId: insuranceClaimsIdSchema,
    projectId: insuranceClaimsIdSchema,
    claim: insuranceClaimsClaimSchema,
    lane: insuranceClaimsClaimDeskLaneSchema,
    tone: insuranceClaimsClaimDeskToneSchema,
    attentionReasons: z.array(insuranceClaimsClaimDeskAttentionReasonSchema),
    claimCoach: insuranceClaimsClaimDeskCoachSummarySchema.nullable(),
    supplements: insuranceClaimsSupplementSummarySchema.nullable(),
});
export const insuranceClaimsClaimDeskCurrencyTotalSchema = z.strictObject({
    currencyCode: z.string().regex(/^[A-Z]{3}$/),
    requestedAmountCents: z.number().int().nonnegative(),
    pendingRequestedAmountCents: z.number().int().nonnegative(),
    approvedAmountCents: z.number().int().nonnegative(),
    approvedUnpaidAmountCents: z.number().int().nonnegative(),
});
export const insuranceClaimsClaimDeskPageSummarySchema = z.strictObject({
    visibleClaimCount: z.number().int().nonnegative(),
    visibleOpenClaimCount: z.number().int().nonnegative(),
    visibleClosedClaimCount: z.number().int().nonnegative(),
    visibleBlockedClaimCount: z.number().int().nonnegative(),
    visibleActionDueClaimCount: z.number().int().nonnegative(),
    visiblePendingSupplementClaimCount: z.number().int().nonnegative(),
    visibleSupplementCount: z.number().int().nonnegative().nullable(),
    visibleAging: z.strictObject({
        under48Hours: z.number().int().nonnegative(),
        from48To95Hours: z.number().int().nonnegative(),
        from96To167Hours: z.number().int().nonnegative(),
        atLeast168Hours: z.number().int().nonnegative(),
    }).nullable(),
    currencyTotals: z.array(insuranceClaimsClaimDeskCurrencyTotalSchema),
});
export const insuranceClaimsClaimDeskSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsClaimDeskCapability),
    generatedAt: z.string().datetime({ offset: true }),
    dataAvailability: z.strictObject({
        claimCoach: z.boolean(),
        supplements: z.boolean(),
    }),
    rows: z.array(insuranceClaimsClaimDeskRowSchema),
    pageSummary: insuranceClaimsClaimDeskPageSummarySchema,
    page: insuranceClaimsPageSchema,
});
export const insuranceClaimsClaimDeskResponseSchema = z.strictObject({
    data: insuranceClaimsClaimDeskSchema,
});
const completedStatuses = new Set([
    'complete', 'skipped',
]);
function time(value) {
    if (value === null)
        return null;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
}
function coachSummary(coach, now) {
    const stored = coach !== null;
    const steps = coach?.steps ?? createDefaultInsuranceClaimsClaimCoachSteps();
    const completedSteps = steps.filter(({ status }) => status === 'complete').length;
    const skippedSteps = steps.filter(({ status }) => status === 'skipped').length;
    const blockedSteps = steps.filter(({ status }) => status === 'blocked').length;
    const openSteps = steps.filter(({ status }) => !completedStatuses.has(status));
    const dueSoonBoundary = now.getTime() + 72 * 60 * 60 * 1_000;
    const overdue = openSteps.filter((step) => {
        const dueAt = time(step.dueAt);
        return dueAt !== null && dueAt < now.getTime();
    });
    const dueSoon = openSteps.filter((step) => {
        const dueAt = time(step.dueAt);
        return dueAt !== null && dueAt >= now.getTime() && dueAt <= dueSoonBoundary;
    });
    const active = steps.find(({ status }) => status === 'blocked') ??
        steps.find(({ status }) => status === 'in_progress') ??
        steps.find(({ status }) => !completedStatuses.has(status)) ?? null;
    const dueSteps = [...overdue, ...dueSoon].toSorted((left, right) => (time(left.dueAt) ?? Number.MAX_SAFE_INTEGER) -
        (time(right.dueAt) ?? Number.MAX_SAFE_INTEGER));
    return insuranceClaimsClaimDeskCoachSummarySchema.parse({
        stored,
        totalSteps: 10,
        completedSteps,
        skippedSteps,
        blockedSteps,
        overdueSteps: overdue.length,
        dueSoonSteps: dueSoon.length,
        completionPercent: Math.round((completedSteps / 10) * 100),
        activeStepKey: active?.stepKey ?? null,
        activeStepTitle: active?.title ?? null,
        nextAction: active?.defaultNextAction ?? null,
        nextDueAt: dueSteps[0]?.dueAt ?? null,
    });
}
function attentionReasons(coach, supplements) {
    const reasons = [];
    if ((coach?.blockedSteps ?? 0) > 0)
        reasons.push('claim-coach-blocked');
    if ((coach?.overdueSteps ?? 0) > 0)
        reasons.push('claim-coach-overdue');
    if ((supplements?.aging.atLeast168Hours ?? 0) > 0) {
        reasons.push('supplement-response-critical');
    }
    else if ((supplements?.aging.from96To167Hours ?? 0) > 0) {
        reasons.push('supplement-response-high');
    }
    else if ((supplements?.aging.from48To95Hours ?? 0) > 0) {
        reasons.push('supplement-response-medium');
    }
    if ((supplements?.byStatus.rejected ?? 0) + (supplements?.byStatus.revised ?? 0) > 0) {
        reasons.push('supplement-revision-needed');
    }
    if ((supplements?.approvedUnpaidAmountCents ?? 0) > 0) {
        reasons.push('approved-supplement-unpaid');
    }
    return reasons;
}
function laneFor(claim, coach, supplements, reasons) {
    if (claim.status !== 'open')
        return 'closeout';
    if (reasons.includes('claim-coach-blocked'))
        return 'blocked';
    if (reasons.some((reason) => [
        'claim-coach-overdue', 'supplement-response-critical', 'supplement-response-high',
    ].includes(reason)))
        return 'action_due';
    if ((supplements?.pendingRequestedAmountCents ?? 0) > 0 ||
        (supplements?.byStatus.rejected ?? 0) > 0 || coach?.activeStepKey === 'supplement') {
        return 'supplement';
    }
    if (coach?.activeStepKey === 'estimate_review' || coach?.activeStepKey === 'carrier_scope') {
        return 'estimate_review';
    }
    if (coach?.activeStepKey === 'invoice' || coach?.activeStepKey === 'mortgage_check')
        return 'money';
    if (coach?.activeStepKey === 'production' || coach?.activeStepKey === 'approval')
        return 'production';
    if (coach?.activeStepKey === 'closeout')
        return 'closeout';
    return 'intake';
}
function toneFor(claim, reasons) {
    if (reasons.includes('claim-coach-blocked') ||
        reasons.includes('supplement-response-critical'))
        return 'risk';
    if (reasons.length > 0)
        return 'attention';
    return claim.status === 'open' ? 'info' : 'good';
}
export function buildInsuranceClaimsClaimDesk(input) {
    const rows = input.claims.map((claim) => {
        const coach = input.claimCoachAvailable
            ? coachSummary(input.claimCoaches.get(claim.id) ?? null, input.now)
            : null;
        const supplements = input.supplementsAvailable
            ? input.supplementSummaries.get(claim.id) ?? null
            : null;
        const reasons = attentionReasons(coach, supplements);
        return insuranceClaimsClaimDeskRowSchema.parse({
            claimId: claim.id,
            projectId: claim.currentVersion.data.projectReference.projectId,
            claim,
            lane: laneFor(claim, coach, supplements, reasons),
            tone: toneFor(claim, reasons),
            attentionReasons: reasons,
            claimCoach: coach,
            supplements,
        });
    });
    const currencyTotals = new Map();
    for (const row of rows) {
        const summary = row.supplements;
        if (summary?.currencyCode === null || summary === null)
            continue;
        const current = currencyTotals.get(summary.currencyCode) ?? {
            requestedAmountCents: 0,
            pendingRequestedAmountCents: 0,
            approvedAmountCents: 0,
            approvedUnpaidAmountCents: 0,
        };
        current.requestedAmountCents += summary.requestedAmountCents ?? 0;
        current.pendingRequestedAmountCents += summary.pendingRequestedAmountCents ?? 0;
        current.approvedAmountCents += summary.approvedAmountCents ?? 0;
        current.approvedUnpaidAmountCents += summary.approvedUnpaidAmountCents ?? 0;
        currencyTotals.set(summary.currencyCode, current);
    }
    const supplementRows = rows.map(({ supplements }) => supplements).filter((summary) => summary !== null);
    const visibleAging = input.supplementsAvailable ? supplementRows.reduce((total, summary) => ({
        under48Hours: total.under48Hours + summary.aging.under48Hours,
        from48To95Hours: total.from48To95Hours + summary.aging.from48To95Hours,
        from96To167Hours: total.from96To167Hours + summary.aging.from96To167Hours,
        atLeast168Hours: total.atLeast168Hours + summary.aging.atLeast168Hours,
    }), { under48Hours: 0, from48To95Hours: 0, from96To167Hours: 0, atLeast168Hours: 0 }) : null;
    return insuranceClaimsClaimDeskSchema.parse({
        contractVersion: insuranceClaimsClaimDeskCapability,
        generatedAt: input.now.toISOString(),
        dataAvailability: {
            claimCoach: input.claimCoachAvailable,
            supplements: input.supplementsAvailable,
        },
        rows,
        pageSummary: {
            visibleClaimCount: rows.length,
            visibleOpenClaimCount: rows.filter(({ claim }) => claim.status === 'open').length,
            visibleClosedClaimCount: rows.filter(({ claim }) => claim.status !== 'open').length,
            visibleBlockedClaimCount: rows.filter(({ lane }) => lane === 'blocked').length,
            visibleActionDueClaimCount: rows.filter(({ lane }) => lane === 'action_due').length,
            visiblePendingSupplementClaimCount: rows.filter(({ supplements }) => (supplements?.pendingRequestedAmountCents ?? 0) > 0).length,
            visibleSupplementCount: input.supplementsAvailable
                ? supplementRows.reduce((total, summary) => total + summary.supplementCount, 0)
                : null,
            visibleAging,
            currencyTotals: [...currencyTotals.entries()].toSorted(([left], [right]) => left.localeCompare(right)).map(([currencyCode, totals]) => ({ currencyCode, ...totals })),
        },
        page: {
            limit: input.query.limit,
            offset: input.query.offset,
            returned: rows.length,
            total: input.total,
        },
    });
}
//# sourceMappingURL=insurance-claims-claim-desk.contract.js.map